import React, { useState, useEffect, useCallback, useRef } from "react";
import { X, PlusCircle, Calendar, CheckCircle, Clock, Edit, Target, Folder } from "lucide-react";
import { toast } from "react-hot-toast";
import { getMilestonesByProject, updateMilestone } from "../../api/milestoneApi";
import KPIFormModal from "./MilestoneFormmodal";

const KPIListModal = ({ isOpen, onClose, project }) => {
    // Reference to the modal's inner container
    const modalRef = useRef(null);

    const [milestones, setMilestones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [selectedMilestone, setSelectedMilestone] = useState(null);

    const fetchMilestones = useCallback(async () => {
        if (!project?._id) return;
        setLoading(true);
        try {
            const data = await getMilestonesByProject(project._id);
            setMilestones(data);
        } catch (error) {
            toast.error(error.message || "Failed to fetch KPI.");
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [project]);

    useEffect(() => {
        if (isOpen) {
            fetchMilestones();
        }
    }, [isOpen, fetchMilestones]);

    // Handle click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onClose]);

    const handleOpenFormModal = (milestone = null) => {
        setSelectedMilestone(milestone);
        setIsFormModalOpen(true);
    };

    const handleCloseFormModal = () => {
        setIsFormModalOpen(false);
        setSelectedMilestone(null);
        fetchMilestones();
    };

    const handleAchieveMilestone = async (milestoneId) => {
        try {
            await updateMilestone(milestoneId, { status: "Completed" });
            toast.success("KPI marked as achieved!");
            fetchMilestones(); // Refresh the list
        } catch (error) {
            toast.error("Failed to update KPI status.");
            console.error(error);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
            <div
                ref={modalRef}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative transform transition-all duration-300 ease-out"
            >
                {/* Header with Orange Theme */}
                <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500">
                    <div className="flex justify-between items-center">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                                <Target className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
                            </div>
                            <div>
                                <h2 className="text-lg sm:text-xl font-bold text-black">
                                    KPI List
                                </h2>
                                <div className="flex items-center mt-1 text-black text-sm">
                                    <Folder className="w-3 h-3 mr-1" />
                                    <span>Programs Department</span>
                                </div>
                            </div>
                        </div>
                        <button 
                            onClick={onClose} 
                            className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="p-4 sm:p-6">
                    <div className="mb-4">
                        <h3 className="text-lg font-medium text-gray-800 mb-2">
                            Project: "{project?.projectTitle}"
                        </h3>
                        <button
                            onClick={() => handleOpenFormModal(null)}
                            className="w-full bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center transition duration-300 shadow-md"
                        >
                            <PlusCircle size={20} className="mr-2" /> Add New KPI
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-8 text-gray-500">
                            <div className="flex justify-center mb-3">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                            </div>
                            Loading KPIs...
                        </div>
                    ) : milestones.length > 0 ? (
                        <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                            {milestones.map((milestone) => (
                                <div key={milestone._id} className="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm hover:bg-orange-50 transition-colors">
                                    <div className="flex items-start">
                                        <div className="mr-3 mt-1 text-orange-500">
                                            <Calendar size={18} />
                                        </div>
                                        <div className="flex-grow">
                                            <h4 className="font-semibold text-gray-800">{milestone.title}</h4>
                                            <div className="flex items-center text-sm text-gray-600 mt-1">
                                                <span>Due: {new Date(milestone.dueDate).toLocaleDateString()}</span>
                                            </div>
                                        </div>
                                        <div className="ml-auto flex flex-col items-end space-y-2">
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                milestone.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                                milestone.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {milestone.status}
                                            </span>
                                            <div className="flex space-x-1">
                                                {milestone.status === 'Completed' && <CheckCircle size={18} className="text-green-500" />}
                                                {milestone.status === 'In Progress' && <Clock size={18} className="text-yellow-500" />}
                                                <button
                                                    onClick={() => handleOpenFormModal(milestone)}
                                                    className="text-gray-500 hover:text-orange-600 p-1 rounded-full transition-colors"
                                                    title="Edit KPI"
                                                >
                                                    <Edit size={18} />
                                                </button>
                                                {milestone.status !== 'Completed' && (
                                                    <button
                                                        onClick={() => handleAchieveMilestone(milestone._id)}
                                                        className="text-green-500 hover:text-green-700 p-1 rounded-full transition-colors"
                                                        title="Mark as Achieved"
                                                    >
                                                        <CheckCircle size={18} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <div className="flex justify-center mb-3">
                                <div className="p-3 bg-orange-100 rounded-full">
                                    <Target className="w-8 h-8 text-orange-500" />
                                </div>
                            </div>
                            <p className="text-gray-500">No KPIs have been added for this project yet.</p>
                            <p className="text-sm text-gray-400 mt-1">Click "Add New KPI" to get started</p>
                        </div>
                    )}
                </div>

                {/* Form Modal */}
                {isFormModalOpen && (
                    <KPIFormModal
                        isOpen={isFormModalOpen}
                        onClose={handleCloseFormModal}
                        projectId={project._id}
                        milestone={selectedMilestone}
                        onMilestoneAdded={handleCloseFormModal}
                        onMilestoneUpdated={handleCloseFormModal}
                    />
                )}
            </div>
        </div>
    );
};

export default KPIListModal;